import { QueryKey, useQuery, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { GetBidReportQuery } from "lib/generated/app-service-gql/graphql";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "lib/hooks/api/utils";
import { useGraphQlQuery } from "../../useGraphQlClient";
import { getBidQualContentForExternalUser } from "../public/getBidQualContentForExternalUser";
import { useExternalBidQuery } from "../public/useExternalBidQuery";

const getBidReport = graphql(`
  query getBidReport($id: String!) {
    getBidReport(id: $id) {
      result {
        groupName
        questions {
          answerId
          autoQualify
          title
          order
          answer {
            answer
          }
          sources {
            fileName
            documentId
            chunkNumber
            locations {
              pageNumber
              bbox {
                x
                y
                width
                height
              }
            }
          }
        }
      }
    }
  }
`);

export type BidNoBidResponse = GetBidReportQuery["getBidReport"]["result"];

export function useGetBidReport(
  id: string,
  options?: UseQueryOptions<GetBidReportQuery, unknown, GetBidReportQuery, QueryKey>,
) {
  const { useExternalQuery, urlShareSignature } = useExternalBidQuery();

  // Primary query for logged-in user
  const { data: reportData, ...rest } = useGraphQlQuery(
    ["get_bid_report", id],
    getBidReport,
    [{ id }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
      enabled: !useExternalQuery,
    },
  );

  // External access fallback (public view)
  const { data: nonLoggedInData, ...other } = useQuery(
    ["get_bid_report", id],
    () => getBidQualContentForExternalUser(id, urlShareSignature),
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      enabled: useExternalQuery,
    },
  );

  if (useExternalQuery) {
    return {
      data: nonLoggedInData?.result,
      ...other,
    };
  } else {
    return {
      data: reportData?.getBidReport?.result,
      ...rest,
    };
  }
}
